import { Box, Stack } from "@mui/material";
import { useApp, useTheme } from "contexts";
import { Custom, Field } from "components";
import { ICategory, IMenu } from "interfaces";
import { useMenu } from "components/field/menu";
import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

// icons
import {
  AddRounded,
  ExpandLessRounded,
  ExpandMoreRounded,
  RemoveRounded,
} from "@mui/icons-material";

type Props = {
  createdBy?: string;
  menu: IMenu[];
  hasError?: boolean;
  readonly?: boolean;
  onUpdate?: (menu: IMenu[]) => void;
};

export const Contribute = ({
  createdBy,
  hasError = false,
  menu,
  onUpdate,
}: Props) => {
  const { t } = useApp();
  const { theme } = useTheme();

  const {
    user,
    menuData,
    configurations,
    handleSelect,
    handleAddItem,
    handleRemoveItem,
    handleChange,
  } = useMenu(menu, true, createdBy, onUpdate);

  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  const toggleExpand = (index: number) => {
    setExpanded((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  return (
    <Box>
      <Stack spacing={theme.spacing.md}>
        <Field.Search
          autoReset
          hasError={hasError}
          onOptionSelect={handleSelect}
          label={t.component.field.menu.search}
          options={configurations}
        />

        {menuData.map((menu: IMenu, i: number) => {
          const readonlyCategories = menu.categories.filter(
            (cat) => cat.suggested_by?._id !== user
          );

          const editableCategories = menu.categories.filter(
            (cat) => cat.suggested_by?._id === user
          );

          const isExpanded = expanded[i];

          return (
            <Stack
              key={i}
              sx={{
                border: 1,
                px: theme.spacing.lg,
                py: theme.spacing.sm,
                borderRadius: theme.border.radius,
                borderColor: theme.color.border.accent,
              }}
            >
              <Box>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={theme.spacing.md}
                  justifyContent="space-between"
                >
                  <Custom.Typography
                    size={theme.font.sm}
                    weight={theme.font.semiBold}
                  >
                    {i + 1}. {menu.configuration.name}
                  </Custom.Typography>
                </Stack>
              </Box>
              <Box mt={theme.spacing.md}>
                <Stack spacing={theme.spacing.sm}>
                  {editableCategories.map((category: ICategory, j: number) => (
                    <Stack
                      key={category._id}
                      direction="row"
                      alignItems="center"
                      gap={theme.spacing.sm}
                    >
                      <Custom.Typography>{j + 1}.</Custom.Typography>
                      <Custom.TextField
                        size="small"
                        required
                        disabled={category.readonly}
                        placeholder={t.placeholder.menu_category}
                        defaultValue={category.name}
                        onBlur={(e) =>
                          handleChange(i, category._id, e.target.value)
                        }
                        sx={{ flex: 1 }}
                      />
                      <Custom.IconButton
                        onClick={() => handleRemoveItem(i, category._id)}
                      >
                        <RemoveRounded />
                      </Custom.IconButton>
                    </Stack>
                  ))}
                  <Box>
                    <Custom.Button secondary onClick={() => handleAddItem(i)}>
                      <AddRounded />
                    </Custom.Button>
                  </Box>
                  {menu.readonly && (
                    <Stack spacing={theme.spacing.xs}>
                      <Box>
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={theme.spacing.sm}
                          justifyContent="space-between"
                        >
                          <Custom.Typography
                            size={theme.font.xs}
                            weight={theme.font.semiBold}
                          >
                            {t.component.field.menu.contribute_suggestion}
                          </Custom.Typography>
                          <Custom.IconButton onClick={() => toggleExpand(i)}>
                            {isExpanded ? (
                              <ExpandLessRounded />
                            ) : (
                              <ExpandMoreRounded />
                            )}
                          </Custom.IconButton>
                        </Stack>
                      </Box>

                      <AnimatePresence initial={false}>
                        {isExpanded && (
                          <Box
                            component={motion.div}
                            pl={theme.spacing.md}
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            {readonlyCategories.map(
                              (category: ICategory, j: number) => (
                                <Stack
                                  key={j}
                                  direction="row"
                                  spacing={theme.spacing.md}
                                >
                                  <Custom.Typography size={theme.font.xs}>
                                    {j + 1}.
                                  </Custom.Typography>
                                  <Custom.Typography size={theme.font.xs}>
                                    {category.name}
                                  </Custom.Typography>
                                </Stack>
                              )
                            )}
                          </Box>
                        )}
                      </AnimatePresence>
                    </Stack>
                  )}
                </Stack>
              </Box>
            </Stack>
          );
        })}
      </Stack>
    </Box>
  );
};
